import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../../components/Layout';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Checkbox } from '../../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { ChevronLeft, Play, CheckCircle, Lock, FileText, FileSpreadsheet, ClipboardCheck, ScrollText } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { mfgAPI, api } from '../../../services/api';
import { toast } from 'sonner';
import { useAuth } from '../../../context/AuthContext';
import RecordAuditTrail from '../../../components/audit/RecordAuditTrail';
import WorkflowActivity from '../../../components/workflow/WorkflowActivity';

const STATUS_COLORS = {
  CREATED: 'bg-slate-100 text-slate-700',
  RELEASED: 'bg-orange-100 text-orange-700',
  PARTIALLY_CONFIRMED: 'bg-amber-100 text-amber-700',
  CONFIRMED: 'bg-blue-100 text-blue-700',
  TECO: 'bg-emerald-100 text-emerald-700',
  CLOSED: 'bg-gray-200 text-gray-600',
};

const ProductionOrderDetail = () => {
  const navigate = useNavigate();
  const { order_id } = useParams();
  const { canWrite } = useAuth();
  const writable = canWrite('production_order');
  const [order, setOrder] = useState(null);
  const [viewTab, setViewTab] = useState('overview');
  const [confirmations, setConfirmations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cForm, setCForm] = useState({ operation_no: '', yield_quantity: '', scrap_quantity: 0, machine_actual: 0, labor_actual: 0, final_confirmation: false });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [oRes, cRes] = await Promise.all([
        mfgAPI.getProductionOrder(order_id),
        mfgAPI.getConfirmations(order_id),
      ]);
      setOrder(oRes.data);
      setConfirmations(cRes.data);
    } catch (e) { toast.error('Failed to load order'); }
    finally { setLoading(false); }
  }, [order_id]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => {
    try { await fn(order_id); toast.success(msg); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
  };

  const download = async (format) => {
    try {
      const url = `/manufacturing/production-orders/${order_id}/export?format=${format}`;
      const res = await api.get(url, { responseType: 'blob' });
      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      const blob = new Blob([res.data]);
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `production_order_${order_id}.${ext}`;
      link.click();
      window.URL.revokeObjectURL(link.href);
    } catch (e) { toast.error('Export failed'); }
  };

  const submitConfirm = async () => {
    if (!cForm.yield_quantity || Number(cForm.yield_quantity) < 0) { toast.error('Yield quantity is required'); return; }
    const payload = {
      order_id,
      operation_no: cForm.operation_no ? Number(cForm.operation_no) : null,
      yield_quantity: Number(cForm.yield_quantity),
      scrap_quantity: Number(cForm.scrap_quantity || 0),
      machine_actual: Number(cForm.machine_actual || 0),
      labor_actual: Number(cForm.labor_actual || 0),
      final_confirmation: !!cForm.final_confirmation,
    };
    try {
      await mfgAPI.createConfirmation(payload);
      toast.success('Confirmation posted');
      setConfirmOpen(false);
      setCForm({ operation_no: '', yield_quantity: '', scrap_quantity: 0, machine_actual: 0, labor_actual: 0, final_confirmation: false });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Confirmation failed'); }
  };

  if (loading || !order) {
    return <Layout><div className="p-10 text-center text-slate-400">Loading…</div></Layout>;
  }

  const canConfirm = writable && ['RELEASED', 'PARTIALLY_CONFIRMED', 'CONFIRMED'].includes(order.system_status);

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="production-order-detail">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/manufacturing/production-orders')}><ChevronLeft className="h-5 w-5" /></Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-slate-900 font-mono">{order.order_id}</h1>
                <Badge className={`${STATUS_COLORS[order.system_status]} border-0`} data-testid="order-status-badge">{order.system_status}</Badge>
              </div>
              <p className="text-sm text-slate-500">{order.material_id} — {order.material_description || ''} · Plant {order.plant_id}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => download('pdf')} data-testid="export-pdf-btn"><FileText className="h-4 w-4 mr-1" /> PDF</Button>
            <Button variant="outline" size="sm" onClick={() => download('excel')} data-testid="export-excel-btn"><FileSpreadsheet className="h-4 w-4 mr-1" /> Excel</Button>
            {writable && order.system_status === 'CREATED' && (
              <Button size="sm" className="bg-orange-600 hover:bg-orange-700" onClick={() => act(mfgAPI.releaseProductionOrder, 'Released')} data-testid="release-order-btn"><Play className="h-4 w-4 mr-1" /> Release</Button>
            )}
            {canConfirm && (
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => setConfirmOpen(true)} data-testid="open-confirm-btn"><ClipboardCheck className="h-4 w-4 mr-1" /> Confirm</Button>
            )}
            {canConfirm && (
              <Button size="sm" variant="outline" className="text-emerald-600" onClick={() => act(mfgAPI.tecoProductionOrder, 'Technically completed')} data-testid="teco-order-btn"><CheckCircle className="h-4 w-4 mr-1" /> TECO</Button>
            )}
            {writable && order.system_status === 'TECO' && (
              <Button size="sm" variant="outline" onClick={() => act(mfgAPI.closeProductionOrder, 'Closed')} data-testid="close-order-btn"><Lock className="h-4 w-4 mr-1" /> Close</Button>
            )}
          </div>
        </div>

        <div className="flex gap-1 border-b border-slate-200">
          <button onClick={() => setViewTab('overview')} data-testid="order-tab-overview"
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${viewTab === 'overview' ? 'border-orange-600 text-orange-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            Overview
          </button>
          <button onClick={() => setViewTab('audit')} data-testid="order-tab-audit"
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors inline-flex items-center gap-1.5 ${viewTab === 'audit' ? 'border-orange-600 text-orange-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            <ScrollText className="w-4 h-4" /> Audit Trail
          </button>
        </div>

        {viewTab === 'overview' && (<>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: 'Order Qty', value: `${order.order_quantity} ${order.uom}` },
            { label: 'Confirmed', value: order.confirmed_quantity },
            { label: 'Scrap', value: order.scrap_quantity },
            { label: 'Start', value: order.planned_start_date },
            { label: 'Finish', value: order.planned_finish_date },
          ].map((s) => (
            <Card key={s.label} className="border-0 shadow-sm"><CardContent className="p-4">
              <p className="text-xs font-medium text-slate-500">{s.label}</p>
              <p className="text-lg font-bold text-slate-900">{s.value}</p>
            </CardContent></Card>
          ))}
        </div>

        <Card className="border-0 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b bg-slate-50 font-semibold text-slate-700">Components</div>
          <Table>
            <TableHeader><TableRow className="bg-white hover:bg-white">
              <TableHead>Item</TableHead><TableHead>Material</TableHead><TableHead>Description</TableHead>
              <TableHead className="text-right">Required</TableHead><TableHead className="text-right">Withdrawn</TableHead><TableHead>UoM</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {order.components.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-400">No components</TableCell></TableRow>
              ) : order.components.map((c) => (
                <TableRow key={c.item_no}>
                  <TableCell>{c.item_no}</TableCell>
                  <TableCell className="font-mono">{c.component_material_id}</TableCell>
                  <TableCell>{c.component_description || '—'}</TableCell>
                  <TableCell className="text-right">{c.required_qty}</TableCell>
                  <TableCell className="text-right">{c.withdrawn_qty}</TableCell>
                  <TableCell>{c.uom}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        <Card className="border-0 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b bg-slate-50 font-semibold text-slate-700">Operations</div>
          <Table>
            <TableHeader><TableRow className="bg-white hover:bg-white">
              <TableHead>Op</TableHead><TableHead>Work Center</TableHead><TableHead>Description</TableHead>
              <TableHead className="text-right">Setup</TableHead><TableHead className="text-right">Machine</TableHead>
              <TableHead className="text-right">Labor</TableHead><TableHead>Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {order.operations.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-6 text-slate-400">No operations</TableCell></TableRow>
              ) : order.operations.map((o) => (
                <TableRow key={o.operation_no}>
                  <TableCell>{o.operation_no}</TableCell>
                  <TableCell className="font-mono">{o.work_center_id}</TableCell>
                  <TableCell>{o.description}</TableCell>
                  <TableCell className="text-right">{o.confirmed_setup}/{o.setup_time}</TableCell>
                  <TableCell className="text-right">{o.confirmed_machine}/{o.machine_time}</TableCell>
                  <TableCell className="text-right">{o.confirmed_labor}/{o.labor_time}</TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{o.op_status}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        <Card className="border-0 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b bg-slate-50 font-semibold text-slate-700">Confirmations ({confirmations.length})</div>
          <Table>
            <TableHeader><TableRow className="bg-white hover:bg-white">
              <TableHead>Confirmation</TableHead><TableHead>Op</TableHead><TableHead className="text-right">Yield</TableHead>
              <TableHead className="text-right">Scrap</TableHead><TableHead>Date</TableHead><TableHead>By</TableHead><TableHead>Final</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {confirmations.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-6 text-slate-400">No confirmations yet</TableCell></TableRow>
              ) : confirmations.map((c) => (
                <TableRow key={c.confirmation_id}>
                  <TableCell className="font-mono text-orange-600">{c.confirmation_id}</TableCell>
                  <TableCell>{c.operation_no ?? '—'}</TableCell>
                  <TableCell className="text-right">{c.yield_quantity}</TableCell>
                  <TableCell className="text-right">{c.scrap_quantity}</TableCell>
                  <TableCell>{c.confirmation_date}</TableCell>
                  <TableCell>{c.posted_by || '—'}</TableCell>
                  <TableCell>{c.final_confirmation ? <CheckCircle className="h-4 w-4 text-emerald-600" /> : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
        </>)}
        {viewTab === 'audit' && (
          <div className="space-y-6">
            <WorkflowActivity doctype="production_order" recordId={order_id} currentState={order.system_status} />
            <RecordAuditTrail doctype="production_order" recordId={order_id} />
          </div>
        )}

        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Post Confirmation — {order.order_id}</DialogTitle>
              <DialogDescription>Record yield and scrap quantities; rolls up to the production order.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div className="col-span-2">
                <Label>Operation</Label>
                <Select value={cForm.operation_no ? String(cForm.operation_no) : 'order'} onValueChange={(v) => setCForm({ ...cForm, operation_no: v === 'order' ? '' : v })}>
                  <SelectTrigger data-testid="confirm-operation-select"><SelectValue placeholder="Order-level" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="order">Order-level (no operation)</SelectItem>
                    {order.operations.map((o) => <SelectItem key={o.operation_no} value={String(o.operation_no)}>Op {o.operation_no} — {o.description}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Yield Quantity *</Label>
                <Input type="number" data-testid="confirm-yield-input" value={cForm.yield_quantity} onChange={(e) => setCForm({ ...cForm, yield_quantity: e.target.value })} />
              </div>
              <div>
                <Label>Scrap Quantity</Label>
                <Input type="number" data-testid="confirm-scrap-input" value={cForm.scrap_quantity} onChange={(e) => setCForm({ ...cForm, scrap_quantity: e.target.value })} />
              </div>
              <div>
                <Label>Machine (actual)</Label>
                <Input type="number" value={cForm.machine_actual} onChange={(e) => setCForm({ ...cForm, machine_actual: e.target.value })} />
              </div>
              <div>
                <Label>Labor (actual)</Label>
                <Input type="number" value={cForm.labor_actual} onChange={(e) => setCForm({ ...cForm, labor_actual: e.target.value })} />
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <Checkbox id="final" checked={cForm.final_confirmation} onCheckedChange={(v) => setCForm({ ...cForm, final_confirmation: v })} data-testid="confirm-final-checkbox" />
                <Label htmlFor="final" className="cursor-pointer">Final confirmation</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
              <Button onClick={submitConfirm} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-confirm-btn">Post Confirmation</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ProductionOrderDetail;
